import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import {
    ADMIN_TIMEZONE,
    buildDefaultDateRange,
} from '@apps/web/components/admin/accounts/trend_chart';
import {
    AgentAnalysisFlowCard,
    AgentAnalysisInsightCards,
    DEFAULT_WINDOW_DAYS,
} from '@apps/web/components/admin/agent_analysis';
import {
    EpisodeClassificationSection,
    episodeFlowToAnalysisCells,
    episodeProductGapsToGapItems,
    type RequestFlowFilter,
    toRequestFlowFilter,
} from '@apps/web/components/admin/episode_classification_analysis';
import { TopFixesCard } from '@apps/web/components/admin/top_fixes_card';
import { type DateRangePickerValue } from '@apps/web/components/ui/date_range_picker';
import { localDayEndIso, localDayStartIso } from '@apps/web/lib/date_utils';
import {
    useAdminAgentCourtRootCauses,
    useEpisodeRecentRequests,
    useEpisodeRequestItems,
    useGlobalAgentAnalysis,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

// Endpoint maximum. The table filters client-side (user, use case,
// outcome), so fetch the full window slice rather than the newest 50.
const RECENT_REQUESTS_LIMIT = 200;

const dateParam = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined);

// The calendar range lives in the URL so a picked window survives reloads
// and pastes as a shareable link.
const agentAnalysisSearchSchema = z.object({
    from: dateParam,
    to: dateParam,
});

export const Route = createFileRoute('/_app/admin/agent-analysis')({
    component: GlobalAgentAnalysisPage,
    validateSearch: agentAnalysisSearchSchema,
    head: () => ({
        meta: [
            {
                title: 'Agent Analysis | Phoebe Admin',
            },
        ],
    }),
});

function GlobalAgentAnalysisPage() {
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const dateRange = useMemo<DateRangePickerValue>(
        () =>
            search.from && search.to
                ? { from: search.from, to: search.to }
                : buildDefaultDateRange(DEFAULT_WINDOW_DAYS),
        [search.from, search.to],
    );
    const { data, isLoading, error } = useGlobalAgentAnalysis(
        dateRange.from,
        dateRange.to,
    );
    // One calendar drives every panel: the KPI tiles, the ribbon, the
    // requests table, and the aggregate cards all query the same window.
    // The picked days become viewer-local instants so "through today"
    // reaches local midnight tonight instead of cutting off at UTC
    // midnight.
    const windowStartTs = localDayStartIso(dateRange.from);
    const windowEndTs = localDayEndIso(dateRange.to);
    const { data: requestItems, isLoading: requestItemsLoading } =
        useEpisodeRequestItems(windowStartTs, windowEndTs, ADMIN_TIMEZONE);
    const { data: recentRequests, isLoading: recentRequestsLoading } =
        useEpisodeRecentRequests(
            windowStartTs,
            windowEndTs,
            RECENT_REQUESTS_LIMIT,
        );
    // Agent Court's clustered root causes for the same window — the ranked
    // "top things to fix" headline above the flow views.
    const {
        data: rootCauses,
        isLoading: rootCausesLoading,
        error: rootCausesError,
    } = useAdminAgentCourtRootCauses(dateRange.from, dateRange.to);
    const [requestFlowFilter, setRequestFlowFilter] =
        useState<RequestFlowFilter | null>(null);
    const episodeFlow = useMemo(
        () => ({
            cells: requestItems
                ? episodeFlowToAnalysisCells(requestItems)
                : undefined,
            isLoading: requestItemsLoading,
            outcomes: requestItems?.outcomes,
        }),
        [requestItems, requestItemsLoading],
    );
    const episodeGaps = useMemo(
        () =>
            requestItems
                ? episodeProductGapsToGapItems(requestItems)
                : undefined,
        [requestItems],
    );
    // Daily series behind the insight cards' line-graph views: product-gap
    // requests and false-success ("claimed but not done") requests.
    const insightTrends = useMemo(
        () =>
            requestItems
                ? {
                      gapsDaily:
                          requestItems.capabilities.find(
                              (entry) => entry.value === 'product_gap',
                          )?.daily_counts ?? [],
                      claimsDaily:
                          requestItems.outcomes.find(
                              (entry) => entry.value === 'false_success',
                          )?.daily_counts ?? [],
                      dateRange,
                  }
                : undefined,
        [requestItems, dateRange],
    );
    // One handler for the page's single global calendar: the picked days
    // land in the URL and every card (top fixes, KPIs, ribbon, requests
    // table) re-queries the same window.
    const handleDateRangeChange = (value: DateRangePickerValue) => {
        void navigate({
            search: { from: value.from, to: value.to },
            replace: true,
        });
    };
    return (
        <div className="flex flex-col gap-4 p-4">
            <div>
                <h1 className="text-lg font-semibold">Agent analysis</h1>
                <p className="text-muted-foreground text-sm">
                    Every judged agent run across all customer organizations
                    (internal Phoebe orgs excluded). The same view exists
                    per-org on each account&rsquo;s Agent tab.
                </p>
            </div>
            <TopFixesCard
                data={rootCauses}
                isLoading={rootCausesLoading}
                error={rootCausesError}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
            />
            <AgentAnalysisFlowCard
                data={data}
                isLoading={isLoading}
                error={error}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                showDateRangePicker={false}
                onSelectionChange={(selection) =>
                    setRequestFlowFilter(toRequestFlowFilter(selection))
                }
                episodeFlow={episodeFlow}
            />
            <EpisodeClassificationSection
                items={requestItems}
                itemsLoading={requestItemsLoading}
                recent={recentRequests}
                recentLoading={recentRequestsLoading}
                requestFlowFilter={requestFlowFilter}
                dateRange={dateRange}
            />
            <AgentAnalysisInsightCards
                data={data}
                gaps={episodeGaps ?? []}
                trends={insightTrends}
                gapsDescription={
                    <span>
                        Product gaps the episode judge recorded across all
                        organizations &mdash; requests users made that Phoebe
                        could not do.
                    </span>
                }
            />
        </div>
    );
}
